import axios, { AxiosError } from "axios";
import axiosInstance from "config/axiosConfig";
import { Task, TaskStatus } from "../types";

export const API_PATHS = {
    TASKS_ASSIGNED: "/tasks/assignee",
    TASKS_CREATED: "/tasks/author",
};

export type ErrorType = {
    error: string;
    message: string;
    status: number;
    timestamp: string;
};

const handleError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ErrorType>;
        return axiosError.response?.data.toString() || "An unknown error occurred";
    }
    return "An unknown error occurred";
};

export const getAssignedTasks = async (assignee: string, status?: TaskStatus): Promise<Task[]> => {
    try {
        const response = await axiosInstance.get(API_PATHS.TASKS_ASSIGNED, {
            params: status ? { assignee, status } : { assignee },
        });
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const getCreatedTasks = async (author: string, status?: TaskStatus): Promise<Task[]> => {
    try {
        const response = await axiosInstance.get(API_PATHS.TASKS_CREATED, {
            params: status ? { author, status } : { author },
        });
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const updateTask = async (taskData: {
    taskId: string;
    assignee: string;
    status: TaskStatus;
    dueDateInUTCEpoch: number;
    description: string;
    title: string;
    watchers: string[];
    labels: string[];
    priority: number;
    isBlocked: boolean;
    closureReason: string;
}): Promise<Task> => {
    try {
        const response = await axiosInstance.put(`/task`, taskData);
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const createTask = async (taskData: Partial<Task>): Promise<Task> => {
    try {
        const response = await axiosInstance.post("/task", taskData);
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const getTaskById = async (taskId: string): Promise<Task> => {
    try {
        const response = await axiosInstance.get('/task', {
            params: {
                taskId: taskId
            }
        });
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const getTasksByResourceId = async (resourceId: string): Promise<Task[]> => {
    try {
        const response = await axiosInstance.get('/tasks/application', {
            params: { tenantResourceId: resourceId }
        });
        return response.data;
    } catch (error) {
        throw new Error(handleError(error));
    }
};

export const getUsers = async () => {
    try {
        const response = await axiosInstance.get('/users');
        return response.data.userResults.map((user: any) => ({
            value: user.username,
            label: user.username,
            email: user.attributes.email || '',
            role: user.attributes['custom:system_role'] || ''
        }));
    } catch (error) {
        throw new Error(handleError(error));
    }
};

